import React, { FunctionComponent } from 'react';
import ResumeSection, { BodyResumeSection } from '../ResumeSection';
import Box from '@material-ui/core/Box';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import Service from 'model/Service';
import { Chip } from 'components/UI';
import Area from './Area';
import { getSimpleDuration, getVariableDuration, formatDuration } from 'utils/ServiceUtils';

interface ResumeDetailsProps {
    service: Service;
    onEdit: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            gap: 16,
            width: '100%',
            display: 'flex',
            alignItems: 'flex-start',

            [theme.breakpoints.down('md')]: {
                flexDirection: 'column'
            }
        },
        tags: {
            gap: 10,
            display: 'flex',
            flexWrap: 'wrap'
        },
        variablesContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
        },
        variableDurationContainer: {
            display: 'flex',
            [theme.breakpoints.down('md')]: {
                marginTop: '4px',
                marginBottom: '4px'
            }
        },
        variableNameContainer: {
            display: 'flex',
            width: '30%',
            [theme.breakpoints.down('md')]: {
                width: '40%'
            }
        },
        variableValueContainer: {
            display: 'flex',
            width: '70%',
            [theme.breakpoints.down('md')]: {
                width: '60%'
            }
        }
    })
);

export const ResumeDetails: FunctionComponent<ResumeDetailsProps> = props => {
    const classes = useStyles();

    const tags = (
        <Box className={classes.tags}>
            {props.service.tags.map(tag => (
                <Box key={tag}>
                    <Chip label={tag} />
                </Box>
            ))}
        </Box>
    );

    // Use helpers for safe duration access
    const simple = getSimpleDuration(props.service);
    const variable = getVariableDuration(props.service);

    const hours = simple?.hours === 0 ? '' : simple?.hours === 1 ? `${simple?.hours} hour` : `${simple?.hours} hours`;

    const minutes =
        simple?.minutes === 0 ? '' : simple?.minutes === 1 ? `${simple?.minutes} minute` : `${simple?.minutes} minutes`;

    const simpleDuration = simple ? `${hours} ${minutes}`.trim() : 'By pet size';

    const durationMessage = (hours: number, minutes: number) => {
        return formatDuration(hours, minutes);
    };

    const variableDuration = simple ? undefined : (
        <Box className={classes.variablesContainer}>
            {variable
                .filter(vp => vp.petSize.name !== undefined)
                .map(variableItem => (
                    <Box
                        key={variableItem.petSize.id}
                        className={classes.variableDurationContainer}
                    >
                        <Box className={classes.variableNameContainer}>{variableItem.petSize.name}</Box>
                        <Box className={classes.variableValueContainer}>
                            {durationMessage(variableItem.duration.hours, variableItem.duration.minutes)}
                        </Box>
                    </Box>
                ))}
        </Box>
    );

    const serviceHoursCompleted = (simple && simple.hours + simple.minutes !== 0) || variable.length !== 0;

    const serviceNameCompleted = props.service.name && props.service.name.length !== 0;

    return (
        <ResumeSection
            title="Service"
            pending={!(serviceNameCompleted && serviceHoursCompleted)}
            onEdit={props.onEdit}
        >
            <BodyResumeSection>
                <Box className={classes.container}>
                    <Area
                        title="Service"
                        info={props.service.name}
                    >
                        {tags}
                    </Area>
                    <Area
                        title="Duration"
                        info={simpleDuration}
                        leftBorder={true}
                        className="noMarginBottom"
                    >
                        {variableDuration}
                    </Area>
                </Box>
            </BodyResumeSection>
        </ResumeSection>
    );
};

export default ResumeDetails;
